import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { Calendar } from 'primereact/calendar';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { RadioButton, RadioButtonChangeEvent } from 'primereact/radiobutton';
import { ApproachDeploymentInput } from 'types/approach';
import { UnifiedFilters } from 'types/filters';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import DeployTargetDialogOrganism from './DeployTargetDialogOrganism';
import './ApproachDeployWizardOrganism.scss';

export type TemplateDeployMode = 'create_new' | 'apply_existing';

export interface TemplateDeployParams {
  mode: TemplateDeployMode;
  deploymentInputValues: Record<string, unknown>;
  parentSphereId?: string;
  newSphereName?: string;
  targetSphereId?: string;
}

export interface ApproachDeployWizardOrganismProps {
  deploymentInputs: ApproachDeploymentInput[];
  approachName: string;
  templateFor?: string;
  targetSphereName?: string;
  /**
   * Approach applicability — passed to `DeployTargetDialogOrganism` in template
   * apply_existing mode so it applies the same `propertyFilters` + `permission:'edit'`
   * as the main deploy flow.
   */
  applicability?: UnifiedFilters | null;
  onDeploy: (values: Record<string, unknown>) => Promise<void>;
  onTemplateDeploy?: (params: TemplateDeployParams) => Promise<void>;
  onCancel: () => void;
  isDeploying?: boolean;
  deployingMessage?: string;
  deployResult?: { success: boolean; message?: string } | null;
}

interface WizardStep {
  key: string;
  labelKey: string;
  inputs: ApproachDeploymentInput[];
}

const ApproachDeployWizardOrganism: React.FC<ApproachDeployWizardOrganismProps> = ({
  deploymentInputs,
  approachName,
  templateFor,
  targetSphereName,
  applicability,
  onDeploy,
  onTemplateDeploy,
  onCancel,
  isDeploying = false,
  deployingMessage,
  deployResult = null,
}) => {
  const { t } = useTranslation();
  const isTemplateApproach = Boolean(templateFor);

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const defaults: Record<string, unknown> = {};
    for (const input of deploymentInputs) {
      if (input.default !== undefined) {
        defaults[input.id] = input.default;
      } else if (input.inputType === 'boolean') {
        defaults[input.id] = false;
      }
    }
    return defaults;
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [deployMode, setDeployMode] = useState<TemplateDeployMode>('create_new');
  const [parentSphereId, setParentSphereId] = useState('');
  const [newSphereName, setNewSphereName] = useState('');
  const [targetSphereId, setTargetSphereId] = useState('');
  const [targetSphereDisplayName, setTargetSphereDisplayName] = useState('');
  const [showTargetPicker, setShowTargetPicker] = useState(false);

  const steps: WizardStep[] = useMemo(() => {
    const required = deploymentInputs.filter((i) => i.required);
    const optional = deploymentInputs.filter((i) => !i.required);
    const result: WizardStep[] = [];

    if (required.length > 0) {
      result.push({
        key: 'required',
        labelKey: 'marketplace.deployWizard.steps.required',
        inputs: required,
      });
    }
    if (optional.length > 0) {
      result.push({
        key: 'optional',
        labelKey: 'marketplace.deployWizard.steps.optional',
        inputs: optional,
      });
    }
    return result;
  }, [deploymentInputs]);

  const currentStep = steps[currentStepIndex];
  const isLastStep = steps.length === 0 || currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const setValue = useCallback((id: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  const validateTemplateFields = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    if (!isTemplateApproach) return newErrors;

    if (deployMode === 'create_new') {
      if (!parentSphereId.trim()) {
        newErrors['__parentSphereId'] = t('marketplace.deployWizard.validation.required');
      }
      if (!newSphereName.trim()) {
        newErrors['__newSphereName'] = t('marketplace.deployWizard.validation.required');
      }
    } else if (!targetSphereId.trim()) {
      newErrors['__targetSphereId'] = t('marketplace.deployWizard.validation.required');
    }
    return newErrors;
  }, [isTemplateApproach, deployMode, parentSphereId, newSphereName, targetSphereId, t]);

  const validateCurrentStep = useCallback((): boolean => {
    const templateErrors = isFirstStep ? validateTemplateFields() : {};

    const stepErrors: Record<string, string> = {};
    if (currentStep) {
      for (const input of currentStep.inputs) {
        if (!input.required) continue;
        const val = values[input.id];
        if (val === undefined || val === null || val === '') {
          stepErrors[input.id] = t('marketplace.deployWizard.validation.required');
        }
      }
    }

    const allErrors = { ...templateErrors, ...stepErrors };
    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  }, [currentStep, values, t, isFirstStep, validateTemplateFields]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) return;
    setCurrentStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
  }, [validateCurrentStep, steps.length]);

  const handleBack = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleDeploy = useCallback(() => {
    if (!validateCurrentStep()) return;

    if (isTemplateApproach && onTemplateDeploy) {
      onTemplateDeploy({
        mode: deployMode,
        deploymentInputValues: values,
        ...(deployMode === 'create_new'
          ? { parentSphereId, newSphereName }
          : { targetSphereId }),
      });
    } else {
      onDeploy(values);
    }
  }, [validateCurrentStep, isTemplateApproach, onTemplateDeploy, onDeploy, values, deployMode, parentSphereId, newSphereName, targetSphereId]);

  const handleDeployModeChange = useCallback((e: RadioButtonChangeEvent) => {
    setDeployMode(e.value as TemplateDeployMode);
    setErrors((prev) => {
      const next = { ...prev };
      delete next['__parentSphereId'];
      delete next['__newSphereName'];
      delete next['__targetSphereId'];
      return next;
    });
  }, []);

  const handleTargetSelect = useCallback((id: string, name: string) => {
    setTargetSphereId(id);
    setTargetSphereDisplayName(name);
    setShowTargetPicker(false);
    setErrors((prev) => {
      if (prev['__targetSphereId']) {
        const next = { ...prev };
        delete next['__targetSphereId'];
        return next;
      }
      return prev;
    });
  }, []);

  const renderTemplateModeSelector = () => {
    if (!isTemplateApproach) return null;

    const typeLabel = templateFor || '';

    return (
      <div className="marketplace-deploy-wizard__template-section">
        <p className="marketplace-deploy-wizard__template-description">
          {t('marketplace.deployWizard.template.description', { type: typeLabel })}
        </p>

        <div className="marketplace-deploy-wizard__template-mode-selector">
          <div className="marketplace-deploy-wizard__template-mode-option">
            <RadioButton
              inputId="deploy-mode-create"
              name="deployMode"
              value="create_new"
              checked={deployMode === 'create_new'}
              onChange={handleDeployModeChange}
              disabled={isDeploying}
            />
            <label htmlFor="deploy-mode-create" className="marketplace-deploy-wizard__template-mode-label">
              {t('marketplace.deployWizard.template.createNew', { type: typeLabel })}
            </label>
          </div>
          <div className="marketplace-deploy-wizard__template-mode-option">
            <RadioButton
              inputId="deploy-mode-existing"
              name="deployMode"
              value="apply_existing"
              checked={deployMode === 'apply_existing'}
              onChange={handleDeployModeChange}
              disabled={isDeploying}
            />
            <label htmlFor="deploy-mode-existing" className="marketplace-deploy-wizard__template-mode-label">
              {t('marketplace.deployWizard.template.applyExisting', { type: typeLabel })}
            </label>
          </div>
        </div>

        <div className="marketplace-deploy-wizard__template-fields">
          {deployMode === 'create_new' ? (
            <>
              <div className={`marketplace-deploy-wizard__field ${errors['__parentSphereId'] ? 'marketplace-deploy-wizard__field--error' : ''}`}>
                <label htmlFor="template-parent-sphere" className="marketplace-deploy-wizard__field-label">
                  {t('marketplace.deployWizard.template.parentSphere')}
                  <span className="marketplace-deploy-wizard__required-mark">*</span>
                </label>
                <span className="marketplace-deploy-wizard__field-description">
                  {t('marketplace.deployWizard.template.parentSphereDescription')}
                </span>
                <div className="marketplace-deploy-wizard__field-input">
                  <input
                    id="template-parent-sphere"
                    type="text"
                    className="marketplace-deploy-wizard__text-input"
                    value={parentSphereId}
                    onChange={(e) => {
                      setParentSphereId(e.target.value);
                      setErrors((prev) => {
                        if (prev['__parentSphereId']) {
                          const next = { ...prev };
                          delete next['__parentSphereId'];
                          return next;
                        }
                        return prev;
                      });
                    }}
                    disabled={isDeploying}
                    placeholder={t('marketplace.deployWizard.template.parentSpherePlaceholder')}
                  />
                </div>
                {errors['__parentSphereId'] && (
                  <span className="marketplace-deploy-wizard__field-error">{errors['__parentSphereId']}</span>
                )}
              </div>

              <div className={`marketplace-deploy-wizard__field ${errors['__newSphereName'] ? 'marketplace-deploy-wizard__field--error' : ''}`}>
                <label htmlFor="template-sphere-name" className="marketplace-deploy-wizard__field-label">
                  {t('marketplace.deployWizard.template.sphereName')}
                  <span className="marketplace-deploy-wizard__required-mark">*</span>
                </label>
                <div className="marketplace-deploy-wizard__field-input">
                  <input
                    id="template-sphere-name"
                    type="text"
                    className="marketplace-deploy-wizard__text-input"
                    value={newSphereName}
                    onChange={(e) => {
                      setNewSphereName(e.target.value);
                      setErrors((prev) => {
                        if (prev['__newSphereName']) {
                          const next = { ...prev };
                          delete next['__newSphereName'];
                          return next;
                        }
                        return prev;
                      });
                    }}
                    disabled={isDeploying}
                    placeholder={t('marketplace.deployWizard.template.sphereNamePlaceholder')}
                  />
                </div>
                {errors['__newSphereName'] && (
                  <span className="marketplace-deploy-wizard__field-error">{errors['__newSphereName']}</span>
                )}
              </div>
            </>
          ) : (
            <div className={`marketplace-deploy-wizard__field ${errors['__targetSphereId'] ? 'marketplace-deploy-wizard__field--error' : ''}`}>
              <label
                className="marketplace-deploy-wizard__field-label"
                htmlFor="deploy-wizard-choose-target-btn"
              >
                {t('marketplace.deployWizard.template.targetSphere', { type: typeLabel })}
                <span className="marketplace-deploy-wizard__required-mark">*</span>
              </label>
              <span className="marketplace-deploy-wizard__field-description">
                {t('marketplace.deployWizard.template.targetSphereDescription', { type: typeLabel })}
              </span>
              <div className="marketplace-deploy-wizard__field-input marketplace-deploy-wizard__target-picker-row">
                {targetSphereDisplayName && (
                  <span
                    data-testid="wizard-selected-target-name"
                    className="marketplace-deploy-wizard__selected-target"
                  >
                    {targetSphereDisplayName}
                  </span>
                )}
                <button
                  type="button"
                  id="deploy-wizard-choose-target-btn"
                  data-testid="wizard-choose-target-btn"
                  className="marketplace-deploy-wizard__choose-target-btn"
                  onClick={() => setShowTargetPicker(true)}
                  disabled={isDeploying}
                >
                  {targetSphereDisplayName
                    ? t('marketplace.deployWizard.template.changeTargetButton')
                    : t('marketplace.deployWizard.template.chooseTargetButton')}
                </button>
              </div>
              {errors['__targetSphereId'] && (
                <span className="marketplace-deploy-wizard__field-error">{errors['__targetSphereId']}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderField = (input: ApproachDeploymentInput) => {
    const label = getLocalizedText(input.label);
    const description = input.description
      ? getLocalizedText(input.description)
      : undefined;
    const fieldId = `wizard-field-${input.id}`;
    const error = errors[input.id];

    return (
      <div
        key={input.id}
        className={`marketplace-deploy-wizard__field ${error ? 'marketplace-deploy-wizard__field--error' : ''}`}
      >
        <label
          htmlFor={fieldId}
          className="marketplace-deploy-wizard__field-label"
        >
          {label}
          {input.required && (
            <span className="marketplace-deploy-wizard__required-mark">*</span>
          )}
        </label>
        {description && (
          <span className="marketplace-deploy-wizard__field-description">
            {description}
          </span>
        )}
        <div className="marketplace-deploy-wizard__field-input">
          {renderInputByType(input, fieldId)}
        </div>
        {error && (
          <span className="marketplace-deploy-wizard__field-error">{error}</span>
        )}
      </div>
    );
  };

  const renderInputByType = (input: ApproachDeploymentInput, fieldId: string) => {
    const val = values[input.id];

    switch (input.inputType) {
      case 'text':
        return (
          <input
            id={fieldId}
            type="text"
            className="marketplace-deploy-wizard__text-input"
            value={(val as string) ?? ''}
            onChange={(e) => setValue(input.id, e.target.value)}
            disabled={isDeploying}
            placeholder={t('marketplace.deployWizard.placeholders.text')}
          />
        );

      case 'number':
        return (
          <InputNumber
            inputId={fieldId}
            value={(val as number) ?? null}
            onValueChange={(e: InputNumberValueChangeEvent) =>
              setValue(input.id, e.value)
            }
            disabled={isDeploying}
            className="marketplace-deploy-wizard__number-input"
            placeholder={t('marketplace.deployWizard.placeholders.number')}
          />
        );

      case 'date':
        return (
          <Calendar
            inputId={fieldId}
            value={(val as Date) ?? null}
            onChange={(e) => setValue(input.id, e.value)}
            disabled={isDeploying}
            className="marketplace-deploy-wizard__date-input"
            dateFormat="yy-mm-dd"
            showIcon
            placeholder={t('marketplace.deployWizard.placeholders.date')}
          />
        );

      case 'date_range':
        return (
          <Calendar
            inputId={fieldId}
            value={(val as Date[] | null) ?? null}
            onChange={(e) => setValue(input.id, e.value)}
            disabled={isDeploying}
            className="marketplace-deploy-wizard__date-range-input"
            dateFormat="yy-mm-dd"
            selectionMode="range"
            showIcon
            placeholder={t('marketplace.deployWizard.placeholders.dateRange')}
          />
        );

      case 'select':
        return (
          <Dropdown
            inputId={fieldId}
            value={(val as string) ?? null}
            options={(input.options || []).map((opt) => ({
              label: getLocalizedText(opt.label),
              value: opt.value,
            }))}
            onChange={(e: DropdownChangeEvent) => setValue(input.id, e.value)}
            disabled={isDeploying}
            className="marketplace-deploy-wizard__select-input"
            placeholder={t('marketplace.deployWizard.placeholders.select')}
          />
        );

      case 'boolean':
        return (
          <div className="marketplace-deploy-wizard__boolean-input">
            <Checkbox
              inputId={fieldId}
              checked={!!val}
              onChange={(e: CheckboxChangeEvent) =>
                setValue(input.id, e.checked)
              }
              disabled={isDeploying}
            />
            <label
              htmlFor={fieldId}
              className="marketplace-deploy-wizard__boolean-label"
            >
              {getLocalizedText(input.label)}
            </label>
          </div>
        );

      case 'person_select':
        return (
          <input
            id={fieldId}
            type="text"
            className="marketplace-deploy-wizard__text-input"
            value={(val as string) ?? ''}
            onChange={(e) => setValue(input.id, e.target.value)}
            disabled={isDeploying}
            placeholder={t('marketplace.deployWizard.placeholders.personSelect')}
          />
        );

      case 'sphere_select':
        return (
          <input
            id={fieldId}
            type="text"
            className="marketplace-deploy-wizard__text-input"
            value={(val as string) ?? ''}
            onChange={(e) => setValue(input.id, e.target.value)}
            disabled={isDeploying}
            placeholder={t('marketplace.deployWizard.placeholders.sphereSelect')}
          />
        );

      default:
        return (
          <input
            id={fieldId}
            type="text"
            className="marketplace-deploy-wizard__text-input"
            value={(val as string) ?? ''}
            onChange={(e) => setValue(input.id, e.target.value)}
            disabled={isDeploying}
          />
        );
    }
  };

  if (deployResult) {
    return (
      <div className="marketplace-deploy-wizard">
        <div className="marketplace-deploy-wizard__result">
          <div
            className={`marketplace-deploy-wizard__result-icon ${
              deployResult.success
                ? 'marketplace-deploy-wizard__result-icon--success'
                : 'marketplace-deploy-wizard__result-icon--error'
            }`}
          >
            {deployResult.success ? '\u2713' : '\u2717'}
          </div>
          <h3 className="marketplace-deploy-wizard__result-title">
            {deployResult.success
              ? t('marketplace.deployWizard.result.successTitle')
              : t('marketplace.deployWizard.result.errorTitle')}
          </h3>
          {deployResult.message && (
            <p className="marketplace-deploy-wizard__result-message">
              {deployResult.message}
            </p>
          )}
          <button
            type="button"
            className="marketplace-deploy-wizard__done-btn"
            onClick={onCancel}
          >
            {t('marketplace.deployWizard.actions.done')}
          </button>
        </div>
      </div>
    );
  }

  if (steps.length === 0 && !isTemplateApproach) {
    return null;
  }

  return (
    <div className="marketplace-deploy-wizard">
      <div className="marketplace-deploy-wizard__header">
        <h3 className="marketplace-deploy-wizard__title">
          {t('marketplace.deployWizard.title', { name: approachName })}
        </h3>
        {targetSphereName && (
          <div className="marketplace-deploy-wizard__target-sphere">
            <i className="pi pi-map-marker" />
            <span>{t('marketplace.deployWizard.targetSphereLabel', { name: targetSphereName })}</span>
          </div>
        )}
        {steps.length > 1 && (
          <div className="marketplace-deploy-wizard__step-indicator">
            {steps.map((step, idx) => (
              <div
                key={step.key}
                className={`marketplace-deploy-wizard__step-dot ${(() => {
                  if (idx === currentStepIndex) return 'marketplace-deploy-wizard__step-dot--active';
                  if (idx < currentStepIndex) return 'marketplace-deploy-wizard__step-dot--completed';
                  return '';
                })()}`}
              >
                <span className="marketplace-deploy-wizard__step-number">
                  {idx + 1}
                </span>
                <span className="marketplace-deploy-wizard__step-label">
                  {t(step.labelKey)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="marketplace-deploy-wizard__body">
        {isFirstStep && renderTemplateModeSelector()}
        {currentStep && (
          <div className="marketplace-deploy-wizard__step-content">
            <p className="marketplace-deploy-wizard__step-description">
              {currentStep.key === 'required'
                ? t('marketplace.deployWizard.descriptions.required')
                : t('marketplace.deployWizard.descriptions.optional')}
            </p>
            {currentStep.inputs.map((input) => renderField(input))}
          </div>
        )}
      </div>

      <div className="marketplace-deploy-wizard__footer">
        {!isFirstStep && (
          <button
            type="button"
            className="marketplace-deploy-wizard__back-btn"
            onClick={handleBack}
            disabled={isDeploying}
          >
            {t('marketplace.deployWizard.actions.back')}
          </button>
        )}
        <div className="marketplace-deploy-wizard__footer-spacer" />
        <button
          type="button"
          className="marketplace-deploy-wizard__cancel-btn"
          onClick={onCancel}
          disabled={isDeploying}
        >
          {t('marketplace.deployWizard.actions.cancel')}
        </button>
        {isLastStep ? (
          <button
            type="button"
            className="marketplace-deploy-wizard__deploy-btn"
            onClick={handleDeploy}
            disabled={isDeploying}
          >
            {isDeploying ? (
              <>
                <span className="marketplace-marketplace__spinner marketplace-deploy-wizard__spinner" />
                {deployingMessage || t('marketplace.deployWizard.actions.deploying')}
              </>
            ) : (
              t('marketplace.deployWizard.actions.deploy')
            )}
          </button>
        ) : (
          <button
            type="button"
            className="marketplace-deploy-wizard__next-btn"
            onClick={handleNext}
            disabled={isDeploying}
          >
            {t('marketplace.deployWizard.actions.next')}
          </button>
        )}
      </div>

      {showTargetPicker && (
        <DeployTargetDialogOrganism
          open={showTargetPicker}
          onClose={() => setShowTargetPicker(false)}
          applicability={applicability ?? null}
          onSelect={handleTargetSelect}
        />
      )}
    </div>
  );
};

export default ApproachDeployWizardOrganism;
